import { useSaladBar } from '@australiangreens/ag-internal-components';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Tooltip,
  Typography,
} from '@mui/material';
import TextField from '@mui/material/TextField';
import { useAtom, WritableAtom } from 'jotai';
import { SetStateAction, useEffect, useState } from 'react';

import CustomDialog from '@components/CustomDialog';
import {
  autoProgressInCallingViewAtom,
  DEFAULT_AUTO_PROGRESS_IN_CALLING_VIEW,
  DEFAULT_TIMEOUT_PERIOD,
  timeoutPeriodAtom,
} from '@hooks/useApp';

type PreferencesDialogProps = {
  open: boolean;
  closeHandler: () => void;
  disableNotRenewing: boolean;
};

const useAtomWithTempState = <T,>(atom: WritableAtom<T, [SetStateAction<T>], void>) => {
  const [atomValue, setAtomValue] = useAtom(atom);
  const [tempValue, setTempValue] = useState(atomValue);

  // [LIST-935] Ensure the temp value is properly initialised
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- keep temp preference in sync with the atom
    setTempValue(atomValue);
  }, [atomValue]);

  const commit = (nextValue?: T) => {
    const valueToCommit = (nextValue !== undefined ? nextValue : tempValue) as typeof tempValue;
    setTempValue(valueToCommit);
    (setAtomValue as (value: typeof atomValue) => void)(valueToCommit);
  };
  const revert = () => setTempValue(atomValue);

  return { commit, revert, value: tempValue, setValue: setTempValue };
};

const PreferencesDialog = ({ open = false, closeHandler }: PreferencesDialogProps) => {
  const { enqueueSuccessNotification } = useSaladBar();

  const {
    value: timeoutPeriod,
    setValue: setTimeoutPeriod,
    commit: commitTimeoutPeriod,
    revert: revertTimeoutPeriod,
  } = useAtomWithTempState(timeoutPeriodAtom);

  const {
    value: autoProgressInCallingView,
    setValue: setAutoProgressInCallingView,
    commit: commitAutoProgressInCallingView,
    revert: revertAutoProgressInCallingView,
  } = useAtomWithTempState(autoProgressInCallingViewAtom);

  const updatePreferencesFromForm = () => {
    commitTimeoutPeriod();
    commitAutoProgressInCallingView();
    enqueueSuccessNotification('Your preferences have been updated');
  };

  const resetForm = () => {
    revertTimeoutPeriod();
    revertAutoProgressInCallingView();
  };

  const handleDialogClose = () => {
    resetForm();
    closeHandler();
  };

  const handleDialogSuccess = async () => {
    updatePreferencesFromForm();
    closeHandler();
  };

  const timeoutErrorText = isNaN(timeoutPeriod)
    ? 'The timeout period must be a number'
    : timeoutPeriod < 0
      ? 'The timeout period must be greater than or equal to zero'
      : '';
  const timeoutError = timeoutErrorText !== '';

  return (
    <CustomDialog
      opendialog={open}
      handleClose={handleDialogClose}
      dialogTitle={'Preferences'}
      handleSuccess={handleDialogSuccess}
      btnSuccessText="Confirm"
      btnCloseText={'Cancel'}
      dialogTestIdSuffix="PreferencesDialog"
      successButtonDisable={timeoutError}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: '1rem' }}>
        <Box>
          <Typography variant="subtitle2">Notifications</Typography>
          <TextField
            margin="dense"
            id="timeoutPeriod"
            label={'Notification timeout period (seconds)'}
            type="number"
            fullWidth
            variant="filled"
            value={timeoutPeriod}
            onChange={(e) => setTimeoutPeriod(parseInt(e.target.value))}
            slotProps={{ htmlInput: { inputMode: 'numeric', pattern: '[0-9]*' } }}
            error={timeoutError}
            helperText={timeoutErrorText}
          />
        </Box>

        <Box>
          <Typography variant="subtitle2">Calling view</Typography>
          <FormControlLabel
            label={
              <Tooltip title='On the calling view for a list, clicking "Skip", "Save" or "Update" will automatically load the next uncalled contact in your list after saving your data'>
                <span>Automatically progress to next contact</span>
              </Tooltip>
            }
            control={
              <Checkbox
                checked={autoProgressInCallingView}
                onChange={(_, checked) => {
                  setAutoProgressInCallingView(checked);
                }}
              />
            }
          />
        </Box>
        <Box>
          <Button
            variant="outlined"
            onClick={() => {
              commitTimeoutPeriod(DEFAULT_TIMEOUT_PERIOD);
              commitAutoProgressInCallingView(DEFAULT_AUTO_PROGRESS_IN_CALLING_VIEW);
            }}
          >
            Reset
          </Button>
          <FormHelperText>This will reset all preferences back to default values</FormHelperText>
        </Box>
      </Box>
    </CustomDialog>
  );
};

export default PreferencesDialog;
